import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from 'components/ui/dialog';

export default function EventTypeFilter({ eventTypes, selectedType, onTypeChange }) {
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);

    if (!eventTypes || eventTypes.length === 0) return null;

    const handleTypeChange = (eventTypeId) => {
        onTypeChange(eventTypeId);
        setFilterDialogOpen(false);
    };

    return (
        <div className="w-[calc(100vw-2rem)] md:w-auto">

            {/* MOBILE LAYOUT */}

            <div className="flex min-w-0 items-center gap-2 md:hidden">

                {/* Mobile: Horizontal Scroll */}

                <div className="flex min-w-0 flex-1 gap-2 items-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <FilterChipList
                        eventTypes={eventTypes}
                        selectedType={selectedType}
                        onTypeChange={onTypeChange}
                    />
                </div>

                {/* Mobile: Dialog Popup */}

                <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                    <DialogTrigger asChild>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="shrink-0"
                            aria-label={t`Show all event filters`}
                        >
                            <SlidersHorizontal aria-hidden />
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                <Trans>Event Categories</Trans>
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-wrap justify-center gap-2">
                            <FilterChipList
                                eventTypes={eventTypes}
                                selectedType={selectedType}
                                onTypeChange={handleTypeChange}
                            />
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* DESKTOP LAYOUT */}

            <div className="hidden flex-wrap justify-center gap-2 md:flex">
                <FilterChipList
                    eventTypes={eventTypes}
                    selectedType={selectedType}
                    onTypeChange={onTypeChange}
                />
            </div>
        </div>
    );
}

function FilterChipList({ eventTypes, selectedType, onTypeChange }) {
    const isAllSelected = !selectedType;

    return (
        <>
            <FilterChip
                selected={isAllSelected}
                onClick={() => onTypeChange(null)}
            >
                <Trans>All Events</Trans>
            </FilterChip>

            {eventTypes.map((et) => {
                const isSelected = String(selectedType) === String(et.event_type_id);

                return (
                    <FilterChip
                        key={et.event_type_id}
                        selected={isSelected}
                        onClick={() => onTypeChange(isSelected ? null : et.event_type_id)}
                    >
                        {et.type_name}
                    </FilterChip>
                );
            })}
        </>
    );
}

function FilterChip({ selected, onClick, children }) {
    return (
        <button
            onClick={onClick}
            aria-pressed={selected}
            className={`
                inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium
                transition-colors cursor-pointer border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                ${selected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent text-foreground border-[rgba(185,191,198,0.4)] hover:border-[rgb(38,43,49)]'
                }
            `}
        >
            {children}
        </button>
    );
}
